import classNames from 'classnames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';
import imgWebapp from 'assets/images/v3-hero-webapp.png';
import imgPhone from 'assets/images/v3-hero-phone.png';
import { CtaButton } from './components/CtaButton';
import { HeroBadge } from './components/HeroBadge';
import { HeroStarfield } from './components/HeroStarfield';

// Optional pill shown above the hero title. `None` (the default) keeps the
// homepage hero unchanged; product pages opt into a branded badge. Add future
// product variants (e.g. MindFeedBusiness) here.
export enum HeroBadgeVariant {
  None = 'none',
  MindFeedClassic = 'mindFeedClassic',
}

// Per-variant brand label (untranslated) + i18n key for the tagline after the dot.
const HERO_BADGES: Record<
  Exclude<HeroBadgeVariant, HeroBadgeVariant.None>,
  { brand: string; taglineKey: string }
> = {
  [HeroBadgeVariant.MindFeedClassic]: {
    brand: 'MindFeed Classic',
    taglineKey: 'pages.home.section8.badgeClassic',
  },
};

const BadgeGlyph = () => (
  <svg viewBox="0 0 24 24" className={styles.badgeGlyph} aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

interface Props {
  /** Branded pill above the title (defaults to no badge). */
  badge?: HeroBadgeVariant;
  /** i18n key for the hero title; may contain a `<highlight>` span. */
  titleKey?: string;
  /** i18n key for the subtitle (the second title line). */
  subtitleKey?: string;
  /** i18n key for the description paragraph below the title. */
  descriptionKey?: string;
  /** Gently float the product mockups and annotation pills (off by default). */
  animateVisuals?: boolean;
}

export const Section8 = ({
  badge = HeroBadgeVariant.None,
  titleKey = 'pages.home.section8.title',
  subtitleKey = 'pages.home.section8.titleLine2',
  descriptionKey = 'pages.home.section8.sub',
  animateVisuals = false,
}: Props) => {
  const t = useTranslations();
  const badgeConfig =
    badge === HeroBadgeVariant.None ? null : HERO_BADGES[badge];
  // Opt-in floating: each element drifts at a slightly different cadence.
  // Disabled under prefers-reduced-motion.
  const float = (animation: string) =>
    animateVisuals ? `${animation} motion-reduce:animate-none` : undefined;
  return (
    <section className={styles.section}>
      {/* Decorative animated starfield (paints the navy bg + glow itself).
          Kept mounted (visibility, not display) so its canvas keeps a real
          size and can resume when the theme flips back to dark. */}
      <HeroStarfield className="invisible marketing-dark:visible" />

      {/* Light-theme glow: soft pastel-blue blobs behind the hero copy. */}
      <div className={styles.lightGlow} aria-hidden="true" />

      <div className={styles.wrap}>
        {badgeConfig && (
          <HeroBadge
            icon={<BadgeGlyph />}
            iconClassName="bg-mf-blue text-white"
            label={badgeConfig.brand}
            tagline={t(badgeConfig.taglineKey)}
          />
        )}
        <h1 className={styles.title}>
          {t.rich(titleKey, {
            highlight: (chunks) => (
              <span className={styles.emphasis}>{chunks}</span>
            ),
          })}
          <br />
          {t(subtitleKey)}
        </h1>
        <p className={styles.sub}>{t(descriptionKey)}</p>
        <div className={styles.cta}>
          <CtaButton
            variant="primary"
            href={appRoutes.LOGIN}
            reportLocation="Homepage Hero"
            withArrow
          >
            {t('pages.home.section8.ctaStart')}
          </CtaButton>
          {/* TODO(SP-668): re-enable when the video is available — https://mindfeed.atlassian.net/browse/SP-668
        <CtaButton variant="outline">
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
            <path d="M8 5v14l11-7z" />
          </svg>
          Watch video
        </CtaButton>
        */}
        </div>

        {/* product stage */}
        <div className={styles.stage.container}>
          {/* Light-theme glow ellipse behind the product mockups. */}
          <div className={styles.stage.lightGlow} aria-hidden="true" />
          <Image
            src={imgWebapp}
            alt="MindFeed desktop app — a memory collection of photos, notes and task lists"
            width={1440}
            height={900}
            className={classNames(
              styles.stage.appshot,
              float('animate-floaty'),
            )}
            sizes="(max-width: 1024px) 100vw, 910px"
            priority
          />

          {/* iOS app overlap */}
          <Image
            src={imgPhone}
            alt="MindFeed iOS app — choosing what to capture: text, quote, link, task and more"
            width={440}
            height={916}
            className={classNames(
              styles.stage.phone,
              float('animate-floaty-slow'),
            )}
            sizes="236px"
          />

          {/* annotation pills */}
          <div
            className={classNames(
              styles.pill.base,
              'top-[-22px] left-[120px]',
              float('animate-floaty'),
            )}
          >
            <span className={classNames(styles.pill.icon, 'bg-[#1f7a52]')}>
              <svg viewBox="0 0 24 24" stroke="#fff">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </span>
            {t('pages.home.section8.pillSaved')}
          </div>
          <div
            className={classNames(
              styles.pill.base,
              'top-[240px] right-[-26px]',
              float('animate-floaty-slow'),
            )}
          >
            <span className={classNames(styles.pill.avatar, 'bg-[#c54b7d]')}>
              MK
            </span>
            {t('pages.home.section8.pillShared')}
          </div>
          <div
            className={classNames(
              styles.pill.base,
              'bottom-[36px] right-[60px]',
              float('animate-floaty-delayed'),
            )}
          >
            <span className={classNames(styles.pill.icon, 'bg-mf-blue')}>
              <svg viewBox="0 0 24 24" stroke="#fff">
                <path d="M12 17.3l-6.2 3.7 1.6-7L2 9.2l7.1-.6L12 2l2.9 6.6 7.1.6-5.4 4.8 1.6 7z" />
              </svg>
            </span>
            {t('pages.home.section8.pillPinned')}
          </div>

          {/* welcome bubble */}
          <div
            className={classNames(
              styles.welcome.container,
              float('animate-floaty-slow'),
            )}
          >
            <div className={styles.welcome.head}>
              <span className={styles.welcome.icon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-4 w-4"
                >
                  <path d="M12 2l1.8 8.2L22 12l-8.2 1.8L12 22l-1.8-8.2L2 12l8.2-1.8z" />
                </svg>
              </span>
              <b className={styles.welcome.title}>
                {t('pages.home.section8.welcomeTitle')}
              </b>
            </div>
            <p className={styles.welcome.text}>
              {t('pages.home.section8.welcomeText')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  // pt clears the fixed header stack (announcement bar 36px + nav 64px).
  // Light theme sits transparent over the page's white + dot grid; dark
  // paints its own navy (the starfield canvas draws over it).
  section:
    'relative overflow-hidden marketing-dark:bg-mf-navy text-center pt-[160px] pb-[90px]',
  // Pastel-blue radial blobs from the light design's hero; hidden in dark
  // where the starfield paints the glow instead.
  lightGlow: classNames(
    'pointer-events-none absolute left-1/2 top-[-120px] h-[800px] w-[1400px] max-w-none -translate-x-1/2',
    'bg-[radial-gradient(46%_58%_at_34%_0%,rgba(189,222,255,0.6)_0%,transparent_65%),radial-gradient(40%_52%_at_70%_4%,rgba(189,222,255,0.45)_0%,transparent_62%),radial-gradient(34%_44%_at_54%_14%,rgba(189,222,255,0.3)_0%,transparent_60%)]',
    'marketing-dark:hidden',
  ),
  wrap: 'relative z-[2] max-w-[1200px] mx-auto px-6 lg:px-10',
  badgeGlyph: 'h-3 w-3 fill-none stroke-current stroke-2',
  title: classNames(
    'mx-auto mb-[22px] max-w-[980px] font-bold tracking-[-0.02em]',
    'text-mf-off-black-600 marketing-dark:text-white',
    'text-4xl leading-tight sm:text-5xl lg:text-[64px] lg:leading-[1.04]',
  ),
  emphasis: 'text-mf-blue marketing-dark:text-mf-coral',
  sub: classNames(
    'mx-auto mb-[30px] max-w-[600px] text-lg leading-relaxed lg:text-xl',
    'text-mf-gray-600 marketing-dark:text-white/72',
  ),
  cta: 'flex flex-wrap justify-center gap-[13px]',
  stage: {
    container:
      'relative mx-auto mt-12 lg:mt-[74px] max-w-[1060px] flex justify-center lg:block lg:min-h-[560px]',
    lightGlow: classNames(
      'pointer-events-none absolute left-1/2 top-[46%] h-[560px] w-[760px] max-w-none -translate-x-1/2 -translate-y-1/2 rounded-full',
      'bg-[radial-gradient(circle,rgba(189,222,255,0.5),transparent_62%)]',
      'marketing-dark:hidden',
    ),
    appshot: classNames(
      'relative z-[2] w-full max-w-[910px] h-auto rounded-[14px] border',
      'border-[#F0F0F0] shadow-[0_20px_50px_rgba(20,30,60,0.08)]',
      'marketing-dark:border-white/10',
      'marketing-dark:shadow-[0_50px_110px_rgba(2,8,30,0.66),0_8px_24px_rgba(0,0,0,0.34)]',
      'lg:mx-auto',
    ),
    phone: classNames(
      'hidden lg:block absolute z-[5] left-[-46px] bottom-[-58px] w-[236px] h-auto',
      'rounded-[42px] drop-shadow-[0_18px_36px_rgba(20,30,60,0.14)]',
      'marketing-dark:drop-shadow-[0_40px_70px_rgba(2,8,30,0.6)]',
    ),
  },
  pill: {
    base: classNames(
      'hidden lg:flex absolute z-[6] items-center gap-[9px] whitespace-nowrap',
      'rounded-xl border px-[13px] py-[9px] text-[13px] font-semibold',
      'border-[#DEDEDE] bg-white text-mf-off-black-600 shadow-[0_16px_36px_rgba(20,30,60,0.16)]',
      'marketing-dark:border-white/[0.13] marketing-dark:bg-[#0b1430] marketing-dark:text-white',
      'marketing-dark:shadow-[0_16px_36px_rgba(0,0,0,0.45)]',
    ),
    icon: 'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-[7px] [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:fill-none [&>svg]:stroke-2',
    avatar:
      'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white',
  },
  welcome: {
    // White card in the light theme; dark card matching the annotation pills
    // (same bg / border / shadow) under `.marketing-dark`.
    container: classNames(
      'hidden lg:block absolute z-[7] top-[-20px] right-[-30px] w-[236px]',
      'rounded-[15px] border p-[15px]',
      'border-[#F0F0F0] bg-white shadow-[0_8px_24px_rgba(20,30,60,0.08)]',
      'marketing-dark:border-white/[0.13] marketing-dark:bg-[#0b1430]',
      'marketing-dark:shadow-[0_16px_36px_rgba(0,0,0,0.45)]',
    ),
    head: 'mb-2 flex items-center gap-[9px]',
    icon: classNames(
      'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px]',
      'bg-[#EBF0F7] text-mf-blue marketing-dark:bg-white/10 marketing-dark:text-mf-yellow',
    ),
    title:
      'text-[13.5px] font-bold text-mf-off-black-600 marketing-dark:text-white',
    text: 'text-[12.5px] leading-normal text-mf-gray-600 marketing-dark:text-white/60',
  },
};
